import React from 'react';
import FormControl from '@mui/material/FormControl';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MuiSelect, { type SelectProps } from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import './MultiSelect.scss';

export interface MultiSelectOption {
    label: React.ReactNode;
    value: string;
}

type TPHMultiSelectProps = Omit<SelectProps<string[]>, 'multiple'> & {
    helperText?: string;
    fullWidth?: boolean;
    options: MultiSelectOption[];
};

export const MultiSelect = React.forwardRef<HTMLDivElement, TPHMultiSelectProps>((props, ref) => {
    const { label, className, disabled, error, readOnly, style, helperText, fullWidth, options, value, ...inputProps } = props;
    const selectedValues = (value as string[]) ?? [];

    return (
        <FormControl ref={ref} disabled={disabled} error={error}
            className={clsx('ms_select', 'ms_multiselect', readOnly && 'readonly', className)}
            variant="filled" style={style} fullWidth={fullWidth}
        >
            <InputLabel>{label}</InputLabel>
            <MuiSelect
                multiple
                value={selectedValues}
                input={<InputBase/>}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={{
                    className: 'ms_select_menu',
                    PaperProps: {
                        className: "ms_select_menu_surface",
                        elevation: 0
                    }
                }}
                renderValue={(selected) => (
                    <Box className="ms_multiselect_chips">
                        {(selected as string[]).map((selectedValue) => {
                            const option = options.find((o) => o.value === selectedValue);
                            return <Chip key={selectedValue} color="primary" label={option?.label ?? selectedValue} size="small" />;
                        })}
                    </Box>
                )}
                readOnly={readOnly}
                {...inputProps}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={selectedValues.includes(option.value)} />
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
            </MuiSelect>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    )
});
